// Sugalarm — Cycle 105: the shared member-avatar component.
//
// Every surface that shows a member's identity uses this one component —
// the kiosk/web header, the mobile header, the Household cards, and the
// Settings → Members rows — so the avatar (and its shield fallback) is
// pixel-consistent everywhere.
//
// The image bytes are served behind auth, so a plain <img src> can't load
// them (no Authorization header). We fetch the blob with the bearer →
// object URL (same technique as ThemeBackground). A small module-level,
// ref-counted cache keyed by `avatarId` means the header + cards sharing
// one avatar fetch it once; the object URL is revoked when the last
// consumer unmounts. Replacement mints a new avatar id, so a changed
// avatar is simply a new cache key (the old one is evicted when nothing
// references it).
//
// When `avatarId` is null — or the fetch 404s (e.g. an unrelated account,
// or a just-deleted avatar) — we render the Sugalarm shield (the `Logo`
// mark) centered in the same rounded-square frame, so the slot is never
// empty or a broken image.

import { useEffect, useState } from "react";
import type { CSSProperties } from "react";

import * as avatarsApi from "../../api/avatars";
import { Logo } from "./Logo";
import styles from "./MemberAvatar.module.css";

interface Props {
  avatarId: string | null;
  /** Member display name — used for the image `alt` / shield alt. */
  name: string;
  /** Frame edge length in px. */
  size: number;
  /** Frame shape. "rounded" (default) = rounded square (header + cards);
   *  "circle" = fully round (Settings → Members, cycle 108a). */
  shape?: "rounded" | "circle";
}

// ── module-level ref-counted object-URL cache ─────────────────────────
interface Entry {
  url: string | null;
  refs: number;
  promise: Promise<string | null>;
}
const cache = new Map<string, Entry>();

function acquire(id: string): Entry {
  let e = cache.get(id);
  if (!e) {
    const entry: Entry = { url: null, refs: 0, promise: Promise.resolve(null) };
    entry.promise = avatarsApi.fetchImageObjectUrl(id).then((u) => {
      entry.url = u;
      return u;
    });
    cache.set(id, entry);
    e = entry;
  }
  e.refs += 1;
  return e;
}

function release(id: string): void {
  const e = cache.get(id);
  if (!e) return;
  e.refs -= 1;
  if (e.refs <= 0) {
    cache.delete(id);
    // The URL may still be resolving — revoke whenever it lands.
    void e.promise.then((u) => {
      if (u) URL.revokeObjectURL(u);
    });
  }
}

export function MemberAvatar({ avatarId, name, size, shape = "rounded" }: Props) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!avatarId) {
      setUrl(null);
      setFailed(false);
      return;
    }
    let active = true;
    setFailed(false);
    const e = acquire(avatarId);
    if (e.url) setUrl(e.url);
    void e.promise.then((u) => {
      if (!active) return;
      if (u) setUrl(u);
      else setFailed(true);
    });
    return () => {
      active = false;
      release(avatarId);
    };
  }, [avatarId]);

  // "circle" → fully round; "rounded" → the panel radius at full size,
  // scaled down for small avatars so a 28px chip isn't over-rounded
  // (capped at the kiosk panel radius, 14px).
  const radius =
    shape === "circle"
      ? "50%"
      : `${Math.round(Math.min(14, size * 0.32))}px`;
  const showImage = !!avatarId && !!url && !failed;

  return (
    <div
      className={styles.frame}
      style={
        {
          "--av-size": `${size}px`,
          "--av-radius": radius,
        } as CSSProperties
      }
    >
      {showImage ? (
        <img className={styles.img} src={url!} alt={name} />
      ) : (
        // The Sugalarm shield, centered on the elevated fill. Sized a bit
        // smaller than the frame so the mark breathes.
        <Logo size={Math.round(size * 0.74)} alt={name} />
      )}
    </div>
  );
}
